import React, { createRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Trans, useTranslation } from 'react-i18next';
import {
  Button,
  ErrorText,
  Flex,
  Heading,
  Input,
  Label,
} from '@visibly/vui';

import { isValidEmail } from '../services';
import ApiService from '../../shared/ApiService';

function Login(props) {
  const {
    partner,
    componentProps,
    ...restProps
  } = props;

  const { t } = useTranslation();
  const formRef = createRef();
  const [[csrfName, csrfValue]] = Object.entries(ApiService.token());
  const [showEmailValidationMessages, setShowEmailValidationMessages] = useState(false);
  const [showPasswordValidationMessages, setShowPasswordValidationMessages] = useState(false);
  const { fields } = componentProps.login;

  const [user, setUser] = useState(restProps.user);

  const errorMessageMap = {
    1: t('login.invalid'),
    2: t('login.locked'),
  };

  const handleInputChange = ({ target }) => {
    setUser({ ...user, [target.id]: target.value });

    if (target.id === 'email' && showEmailValidationMessages) {
      setShowEmailValidationMessages(false);
    }

    if (target.id === 'password' && showPasswordValidationMessages) {
      setShowPasswordValidationMessages(false);
    }
  };

  const submitHandler = (e) => {
    if (!isValid()) {
      e.preventDefault();
    }
  };

  const isValid = () => {
    const emailValidationResult = isValidEmail(user.email);
    const passwordValidationResult = user.password !== '';

    setShowEmailValidationMessages(!emailValidationResult);
    setShowPasswordValidationMessages(!passwordValidationResult);

    return emailValidationResult && passwordValidationResult;
  };

  const errorMessage = (code) => errorMessageMap[code];

  return (
    <div className="prelogin__login">
      <a
        href={partner.urls.root}
        className="prelogin__login__logo"
      >
        <img src={partner.urls.logo} alt={partner.name} />
      </a>

      <form
        action={partner.urls.login}
        method="post"
        ref={formRef}
        className={classNames({
          'viz-form': true,
          'viz-form--show-validation': componentProps.errors,
        })}
        noValidate
        onSubmit={submitHandler}
      >
        <Heading variant='h4' style={{ textAlign: 'center' }}>{t('login.heading')}</Heading>

        <input type="hidden" name={csrfName} value={csrfValue} />
        <Flex variant='column' spacingVertical='lg'>
          {componentProps.errors && <ErrorText spacingVertical='lg' message={errorMessage(componentProps.errors)} />}
          <Label
            htmlFor='email'
            error={showEmailValidationMessages && user.email === ''}
          >{
            t('login.fields.email.label')}
          </Label>
          <Input
            {...fields.email}
            id="email"
            type="email"
            name="user[email]"
            isPhi
            value={user.email}
            required
            onChange={handleInputChange}
            error={showEmailValidationMessages && user.email === ''}
          />
          {showEmailValidationMessages && <ErrorText message={t('login.fields.email.validationMessage')} />}

        </Flex>
        <Flex variant='column' spacingVertical='sm'>
          <Label
            htmlFor='password'
            error={showEmailValidationMessages && user.email === ''}
          >
            {t('login.fields.password.label')}
          </Label>
          <Input
            {...fields.password}
            id="password"
            type="password"
            name="user[password]"
            isPhi
            value={user.password}
            required
            onChange={handleInputChange}
            error={showPasswordValidationMessages && user.password === ''}
          />
          {showPasswordValidationMessages && <ErrorText message={t('login.fields.password.validationMessage')} />}
        </Flex>
        <div className="viz-text viz-text--secondary">
          <Trans i18nKey="login.forgotPassword">
            0
            <a href={partner.urls.resetPassword} className="viz-link branded__link">1</a>
          </Trans>
        </div>

        <div className="prelogin__login__policies viz-text viz-text--secondary">
          <Trans i18nKey="login.agreeToPolicies">
            0

            <a
              href={partner.urls.termsOfUse}
              className="viz-link branded__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              1
            </a>

            2

            <a
              href={partner.urls.privacyPractices}
              className="viz-link branded__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              3
            </a>

            4

            <a
              href={partner.urls.privacyPolicy}
              className="viz-link branded__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              5
            </a>

            6

            <a
              href={partner.urls.serviceAgreement}
              className="viz-link branded__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              7
            </a>

            8
          </Trans>
        </div>

        <div className="prelogin__login__submit-button">
          <Button type="submit">
            {t('login.callToAction')}
          </Button>
        </div>

        <div className="prelogin__login__signup viz-text viz-text--secondary">
          <Trans i18nKey="login.signUp">
            0
            <a href={partner.urls.signup} className="viz-link branded__link">1</a>
          </Trans>
        </div>
      </form>
    </div>
  );
}

Login.propTypes = {
  partner: PropTypes.shape({
    name: PropTypes.string.isRequired,
    urls: PropTypes.shape({
      root: PropTypes.string.isRequired,
      login: PropTypes.string.isRequired,
      signup: PropTypes.string.isRequired,
      resetPassword: PropTypes.string.isRequired,
      termsOfUse: PropTypes.string.isRequired,
      privacyPractices: PropTypes.string.isRequired,
      privacyPolicy: PropTypes.string.isRequired,
      serviceAgreement: PropTypes.string.isRequired,
      logo: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,

  user: PropTypes.shape({
    email: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
  }),

  componentProps: PropTypes.exact({
    login: PropTypes.exact({
      fields: PropTypes.exact({
        email: PropTypes.shape({}),
        password: PropTypes.shape({}),
      }),
    }),
    errors: PropTypes.string,
  }),
};

Login.defaultProps = {
  user: { email: '', password: '' },
  componentProps: {
    login: {
      fields: { email: {}, password: {} },
    },
    errors: null,
  },
};

export default Login;
